import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  CheckCircle2,
  Eye,
  FileImage,
  GripVertical,
  Loader2,
  Pencil,
  Plus,
  Tag,
  Trash2,
  Upload,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { extractInstitutionCategoriesFromApiResponse, institutionCategoriesApiPath, institutionCategoriesCrudPath } from '@/lib/institution-categories-from-api';
import {
  localizedInstitutionCategoryDescription,
  localizedInstitutionCategoryName,
} from '@/lib/institution-category-i18n';
import { imagesApi } from '@/api/images.api';
import { toast } from '@/components/ui/Toast';
import { TrainerRightPanelHeader } from '@/components/dashboard/TrainerRightPanelHeader';

interface Category {
  id: string;
  name: string;
  color: string;
  description: string;
  coverImageUrl: string | null;
  usageCount: number;
  nameTranslations?: unknown;
  descriptionTranslations?: unknown;
}

const INITIAL_CATEGORIES: Category[] = [
  {
    id: '1',
    name: 'Sécurité',
    color: '#EF4444',
    description: 'Formations liées à la sécurité informatique et physique',
    coverImageUrl: null,
    usageCount: 12,
  },
  {
    id: '2',
    name: 'Management',
    color: '#3B82F6',
    description: "Leadership, gestion d'équipe, communication managériale",
    coverImageUrl: null,
    usageCount: 8,
  },
  {
    id: '3',
    name: 'Bureautique',
    color: '#10B981',
    description: 'Excel, Word, PowerPoint et outils collaboratifs',
    coverImageUrl: null,
    usageCount: 15,
  },
  {
    id: '4',
    name: 'Conformité',
    color: '#F59E0B',
    description: 'RGPD, réglementations, normes qualité',
    coverImageUrl: null,
    usageCount: 6,
  },
  {
    id: '5',
    name: 'Soft Skills',
    color: '#8B5CF6',
    description: 'Communication, créativité, résolution de problèmes',
    coverImageUrl: null,
    usageCount: 10,
  },
  {
    id: '6',
    name: 'Bien-être',
    color: '#EC4899',
    description: 'Gestion du stress, ergonomie, qualité de vie au travail',
    coverImageUrl: null,
    usageCount: 4,
  },
];

const COLOR_OPTIONS = [
  '#EF4444',
  '#F59E0B',
  '#10B981',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
  '#6366F1',
  '#14B8A6',
  '#F97316',
  '#64748B',
];

function normalizeCategoryRows(raw: unknown): Category[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c: any) => ({
      id: String(c.id),
      name: String(c.name ?? ''),
      color: String(c.color ?? '#3B82F6'),
      description: String(c.description ?? ''),
      coverImageUrl:
        typeof c.coverImageUrl === 'string' && c.coverImageUrl.trim().length > 0 ? c.coverImageUrl.trim() : null,
      usageCount: Number.isFinite(Number(c.usageCount)) ? Number(c.usageCount) : 0,
      nameTranslations: c.nameTranslations,
      descriptionTranslations: c.descriptionTranslations,
    }))
    .filter((c: Category) => c.name.trim().length > 0);
}

export default function InstitutionCategoriesPanel() {
  const { t, i18n } = useTranslation();

  const [categories, setCategories] = useState<Category[]>(() => {
    const saved = localStorage.getItem('institution_categories');
    return saved ? JSON.parse(saved) : INITIAL_CATEGORIES;
  });
  const [apiMode, setApiMode] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      const response = await api.get<unknown>(institutionCategoriesApiPath());
      const rows = extractInstitutionCategoriesFromApiResponse(response);
      setCategories(normalizeCategoryRows(rows));
      setApiMode(true);
      return true;
    } catch {
      setApiMode(false);
      return false;
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('institution_categories', JSON.stringify(categories));
  }, [categories]);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'edit' | 'preview'>('edit');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [previewCategory, setPreviewCategory] = useState<Category | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewPodcasts, setPreviewPodcasts] = useState<
    Array<{
      id: string;
      title: string;
      description: string;
      episodeCount: number;
      subscriberCount: number;
      updatedAt: string;
      coverImageUrl: string | null;
    }>
  >([]);
  const [previewFlows, setPreviewFlows] = useState<
    Array<{
      id: string;
      title: string;
      description: string;
      status: string;
      participantsCount: number;
      teacherName: string;
      updatedAt: string;
    }>
  >([]);

  const [form, setForm] = useState({
    name: '',
    color: '#3B82F6',
    description: '',
    coverImageUrl: null as string | null,
  });
  const [coverUploading, setCoverUploading] = useState(false);
  const coverFileRef = useRef<HTMLInputElement>(null);

  const fieldCls =
    'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[color:var(--inst-player-primary,#0C1C38)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_18%,transparent)] dark:border-[#B6CAEC] dark:bg-[#122644] dark:text-white';

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setDrawerMode('edit');
    setEditingId(null);
    setError(null);
    setSuccess(false);
    setForm({ name: '', color: '#3B82F6', description: '', coverImageUrl: null });
    setPreviewCategory(null);
    setPreviewLoading(false);
    setPreviewError(null);
    setPreviewPodcasts([]);
    setPreviewFlows([]);
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving && !coverUploading) closeDrawer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen, saving, coverUploading, closeDrawer]);

  const coverPreviewResolved = form.coverImageUrl ? imagesApi.resolveUrl(form.coverImageUrl) : '';

  const onPickCategoryCover = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    setCoverUploading(true);
    try {
      const res = await imagesApi.upload(file, 'general');
      const row = res && typeof res === 'object' && 'data' in res ? (res as { data?: { url?: string } }).data : null;
      const url = typeof row?.url === 'string' ? row.url : null;
      if (url) setForm((prev) => ({ ...prev, coverImageUrl: url }));
    } catch {
      toast.error(t('institution.categories.coverUploadError', 'Échec du téléversement de la couverture.'));
    } finally {
      setCoverUploading(false);
    }
  };

  const openCreate = useCallback(() => {
    setDrawerMode('edit');
    setEditingId(null);
    setForm({ name: '', color: '#3B82F6', description: '', coverImageUrl: null });
    setError(null);
    setSuccess(false);
    setDrawerOpen(true);
  }, []);

  const openEdit = useCallback((cat: Category) => {
    setDrawerMode('edit');
    setEditingId(cat.id);
    setForm({
      name: cat.name,
      color: cat.color,
      description: cat.description,
      coverImageUrl: cat.coverImageUrl,
    });
    setError(null);
    setSuccess(false);
    setDrawerOpen(true);
  }, []);

  const openPreview = useCallback((cat: Category) => {
    setDrawerMode('preview');
    setEditingId(null);
    setPreviewCategory(cat);
    setError(null);
    setSuccess(false);
    setPreviewError(null);
    setPreviewLoading(false);
    setPreviewPodcasts([]);
    setPreviewFlows([]);
    setDrawerOpen(true);
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;
    if (drawerMode !== 'preview') return;
    if (!previewCategory) return;

    if (!apiMode) {
      setPreviewError(t('institution.categories.previewApiModeOff', "Prévisualisation indisponible (mode local)."));
      return;
    }

    let mounted = true;
    setPreviewLoading(true);
    setPreviewError(null);
    api
      .get<any>(`${institutionCategoriesCrudPath()}/${previewCategory.id}/preview`)
      .then((resp) => {
        if (!mounted) return;
        const raw: any = resp?.data ?? resp;
        setPreviewPodcasts(Array.isArray(raw?.podcasts) ? raw.podcasts : []);
        setPreviewFlows(Array.isArray(raw?.flows) ? raw.flows : []);
      })
      .catch(() => {
        if (!mounted) return;
        setPreviewError(t('institution.categories.previewError', "Erreur lors du chargement de l’aperçu."));
      })
      .finally(() => {
        if (!mounted) return;
        setPreviewLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [apiMode, drawerMode, drawerOpen, previewCategory, t]);

  const handleDelete = useCallback(
    (id: string) => {
      if (apiMode) {
        api
          .delete(`${institutionCategoriesCrudPath()}/${id}`)
          .then(() => {
            void loadCategories();
          })
          .catch(() => {});
      } else {
        setCategories((prev) => prev.filter((c) => c.id !== id));
      }
    },
    [apiMode, loadCategories],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!form.name.trim()) return;
      if (saving) return;

      setSaving(true);
      setError(null);
      setSuccess(false);

      const payload = {
        name: form.name.trim(),
        color: form.color,
        description: form.description.trim(),
        coverImageUrl: form.coverImageUrl,
      };

      try {
        if (apiMode) {
          if (editingId) {
            await api.patch(`${institutionCategoriesCrudPath()}/${editingId}`, payload);
          } else {
            await api.post(institutionCategoriesCrudPath(), payload);
          }
          await loadCategories();
        } else {
          if (editingId) {
            setCategories((prev) =>
              prev.map((c) =>
                c.id === editingId ? { ...c, ...payload } : c,
              ),
            );
          } else {
            const newCat: Category = {
              id: Date.now().toString(),
              usageCount: 0,
              ...payload,
              coverImageUrl: payload.coverImageUrl ?? null,
            };
            setCategories((prev) => [...prev, newCat]);
          }
        }

        setSuccess(true);
        setTimeout(() => closeDrawer(), 350);
      } catch {
        setError(t('institution.categories.saveError', "Erreur lors de la sauvegarde."));
      } finally {
        setSaving(false);
      }
    },
    [apiMode, editingId, form, loadCategories, saving, closeDrawer, t],
  );

  const drawerTitle =
    drawerMode === 'preview' && previewCategory
      ? `${t('institution.categories.preview', 'Aperçu')}: ${localizedInstitutionCategoryName(previewCategory, i18n.language)}`
      : editingId
        ? t('institution.categories.edit', 'Modifier la catégorie')
        : t('institution.categories.new', 'Nouvelle catégorie');

  const drawerDescription = useMemo(() => {
    if (drawerMode === 'preview' && previewCategory) {
      return `${t('institution.categories.previewDesc', 'Podcasts et parcours reliés à cette catégorie.')}${
        typeof previewCategory.usageCount === 'number'
          ? ` ${previewCategory.usageCount} ${t('institution.categories.usageItems', 'élément(s)')}`
          : ''
      }`;
    }

    if (editingId) return form.name || drawerTitle;
    return 'Renseignez un titre, une couleur et une description. Vous pourrez ensuite l’utiliser pour classer les podcasts.';
  }, [drawerMode, previewCategory, editingId, form.name, drawerTitle, t]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0C1C38] dark:text-white">
            {t('institution.categories.title')}
          </h1>
          <p className="mt-1 text-sm text-[#5B6472] dark:text-[#B6CAEC]">
            {t('institution.categories.pageSubtitle')}
          </p>
        </div>

        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-xl bg-[color:var(--inst-player-primary,#0C1C38)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[color:var(--inst-player-primary-hover,#19335E)]"
        >
          <Plus className="h-4 w-4" aria-hidden /> {t('institution.categories.new')}
        </button>
      </div>

      {/* Category list */}
      <div className="space-y-2">
        {categories.map((cat) => {
          const rowDesc = localizedInstitutionCategoryDescription(cat, i18n.language);
          return (
          <div
            key={cat.id}
            className="flex items-center gap-4 rounded-xl border border-[#E8EDF5] bg-white p-4 transition-colors group hover:border-primary-300 dark:border-[#19335E] dark:bg-[#122644] dark:hover:border-primary-600"
            onClick={() => openPreview(cat)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') openPreview(cat);
            }}
          >
            <GripVertical className="h-4 w-4 flex-shrink-0 cursor-grab text-[#5B6472]/50 dark:text-[#B6CAEC]" />
            {cat.coverImageUrl ? (
              <div className="h-12 w-16 flex-shrink-0 overflow-hidden rounded-lg border border-[#E8EDF5] dark:border-[#B6CAEC]">
                <img
                  src={imagesApi.resolveUrl(cat.coverImageUrl)}
                  alt=""
                  className="h-full w-full object-cover"
                />
              </div>
            ) : null}
            <div
              className="h-4 w-4 flex-shrink-0 rounded-full"
              style={{ backgroundColor: cat.color }}
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-[#0C1C38] dark:text-white">
                  {localizedInstitutionCategoryName(cat, i18n.language)}
                </span>
                <span className="text-xs text-[#5B6472]/70 dark:text-[#B6CAEC]">
                  {t('institution.categories.usageTimes', {
                    count: cat.usageCount,
                  })}
                </span>
              </div>
              {rowDesc ? (
                <p className="mt-1 truncate text-sm text-[#5B6472] dark:text-[#B6CAEC]">
                  {rowDesc}
                </p>
              ) : null}
            </div>

            <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(cat);
                }}
                className="rounded-lg p-1.5 text-[#5B6472]/70 hover:bg-[#F0F4FA] hover:text-[#5B6472] dark:hover:bg-[#19335E] dark:hover:text-white"
                title={t('institution.categories.edit', 'Modifier')}
              >
                <Pencil className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(cat.id);
                }}
                className="rounded-lg p-1.5 text-[#5B6472]/70 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                title={t('institution.common.delete', 'Supprimer')}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          );
        })}
      </div>

      {drawerOpen && (
        <>
          {createPortal(
            <div className="fixed inset-0 z-50 flex justify-end rtl:justify-start" role="dialog" aria-modal="true">
              {/* Backdrop */}
              <div
                className="absolute inset-0 bg-black/20 backdrop-blur-[2px]"
                onClick={() => !saving && !coverUploading && closeDrawer()}
                aria-hidden
              />

              <div className="relative z-10 flex h-full w-full max-w-md flex-col border-s border-slate-200 bg-white shadow-2xl dark:border-[#19335E] dark:bg-[#0C1C38] ltr:animate-slide-in-right rtl:animate-slide-in-left">
                <TrainerRightPanelHeader
                  icon={drawerMode === 'preview' ? Eye : Tag}
                  title={drawerTitle}
                  description={drawerDescription}
                  onClose={() => !saving && !coverUploading && closeDrawer()}
                  closeDisabled={drawerMode === 'edit' ? saving || coverUploading : false}
                  titleId="institution-category-drawer-title"
                />

                {drawerMode === 'preview' ? (
                  <div className="flex min-h-0 flex-1 flex-col">
                    <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
                      {previewLoading ? (
                        <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('institution.categories.previewLoading', 'Chargement de l’aperçu…')}
                        </div>
                      ) : null}

                      {previewError ? (
                        <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
                          <AlertCircle className="h-4 w-4 shrink-0" />
                          {previewError}
                        </div>
                      ) : null}

                      {!previewLoading && !previewError ? (
                        <>
                          <section className="space-y-2">
                            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                              {t('institution.categories.previewPodcasts', 'Podcasts')}
                            </h2>
                            {previewPodcasts.length === 0 ? (
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                {t('institution.categories.previewNoPodcasts', 'Aucun podcast rattaché à cette catégorie.')}
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {previewPodcasts.slice(0, 8).map((p) => (
                                  <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-3 dark:border-[#19335E] dark:bg-[#0C1C38]">
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="min-w-0">
                                        <div className="truncate text-sm font-medium text-slate-900 dark:text-white">{p.title}</div>
                                        {p.description ? (
                                          <div className="mt-1 line-clamp-2 text-xs text-slate-600 dark:text-slate-400">{p.description}</div>
                                        ) : null}
                                      </div>
                                      <div className="flex shrink-0 flex-col items-end gap-1">
                                        <span className="rounded-full bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)] px-2 py-0.5 text-[11px] font-medium text-[color:var(--inst-player-primary,#0C1C38)]">
                                          {p.episodeCount} {t('institution.categories.episodes', 'épisode(s)')}
                                        </span>
                                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                                          {p.subscriberCount} {t('institution.categories.subscribers', 'abonné(s)')}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {previewPodcasts.length > 8 ? (
                                  <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {t('institution.categories.previewMore', '… et {{n}} autres', { n: previewPodcasts.length - 8 })}
                                  </p>
                                ) : null}
                              </div>
                            )}
                          </section>

                          <section className="space-y-2">
                            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                              {t('institution.categories.previewFormations', 'Parcours / formations')}
                            </h2>
                            {previewFlows.length === 0 ? (
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                {t('institution.categories.previewNoFlows', 'Aucune formation rattachée à cette catégorie.')}
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {previewFlows.slice(0, 8).map((f) => (
                                  <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-3 dark:border-[#19335E] dark:bg-[#0C1C38]">
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="min-w-0">
                                        <div className="truncate text-sm font-medium text-slate-900 dark:text-white">{f.title}</div>
                                        <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                                          {f.teacherName ? `${f.teacherName} · ` : ''}
                                          {f.participantsCount} {t('institution.categories.learners', 'apprenant(s)')}
                                        </div>
                                        {f.description ? (
                                          <div className="mt-1 line-clamp-2 text-xs text-slate-600 dark:text-slate-400">{f.description}</div>
                                        ) : null}
                                      </div>
                                      <div className="flex shrink-0 flex-col items-end gap-1">
                                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                                          {f.status}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {previewFlows.length > 8 ? (
                                  <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {t('institution.categories.previewMore', '… et {{n}} autres', { n: previewFlows.length - 8 })}
                                  </p>
                                ) : null}
                              </div>
                            )}
                          </section>
                        </>
                      ) : null}
                    </div>

                    <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/90 px-5 py-4 dark:border-[#19335E] dark:bg-[#0C1C38]/80 sm:flex-row sm:justify-end sm:gap-3 sm:px-6">
                      <button
                        type="button"
                        onClick={closeDrawer}
                        disabled={previewLoading}
                        className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-[#B6CAEC] dark:bg-[#122644] dark:text-slate-200 dark:hover:bg-[#19335E]"
                      >
                        {t('institution.common.close', 'Fermer')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="flex min-h-0 flex-1 flex-col"
                  >
                    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5 sm:px-6">
                      {error ? (
                        <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
                          <AlertCircle className="h-4 w-4 shrink-0" />
                          {error}
                        </div>
                      ) : null}

                      {success ? (
                        <div className="flex items-center gap-2 rounded-lg bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)] px-3 py-2 text-sm font-medium text-[color:var(--inst-player-primary,#0C1C38)]">
                          <CheckCircle2 className="h-4 w-4 shrink-0" />
                          {t('institution.common.save', 'Enregistré')}
                        </div>
                      ) : null}

                      <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          {t('institution.common.name', 'Nom')}
                        </label>
                        <input
                          value={form.name}
                          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                          placeholder={t('institution.categories.namePlaceholder', 'Nom de la catégorie')}
                          autoFocus
                          className={fieldCls}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          {t('institution.categories.color', 'Couleur')}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {COLOR_OPTIONS.map((c) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => setForm((prev) => ({ ...prev, color: c }))}
                              className={cn(
                                'h-8 w-8 rounded-full border-2 transition-transform',
                                form.color === c
                                  ? 'border-[#0C1C38] dark:border-white scale-110'
                                  : 'border-transparent',
                              )}
                              style={{ backgroundColor: c }}
                              aria-label={`Couleur ${c}`}
                            />
                          ))}
                        </div>
                      </div>

                      <div>
                        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          {t('institution.categories.coverLabel', 'Image de couverture')}
                        </span>
                        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-start">
                          <div className="relative h-28 w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-100 dark:border-[#B6CAEC] dark:bg-[#122644]/50 sm:h-32 sm:w-44 sm:shrink-0">
                            {coverPreviewResolved ? (
                              <img src={coverPreviewResolved} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-slate-400">
                                <FileImage className="h-8 w-8 opacity-40" />
                              </div>
                            )}
                            {coverUploading ? (
                              <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-[#0C1C38]/80">
                                <Loader2 className="h-6 w-6 animate-spin text-[color:var(--inst-player-primary,#0C1C38)]" />
                              </div>
                            ) : null}
                          </div>
                          <div className="flex min-w-0 flex-1 flex-col gap-2">
                            <input
                              ref={coverFileRef}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={onPickCategoryCover}
                            />
                            <button
                              type="button"
                              onClick={() => coverFileRef.current?.click()}
                              disabled={saving || coverUploading}
                              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-[#B6CAEC] dark:bg-[#122644] dark:text-slate-200 dark:hover:bg-[#19335E]"
                            >
                              <Upload className="h-4 w-4" />
                              {t('eduflow.list.coverUpload', 'Choisir une image')}
                            </button>
                            {form.coverImageUrl ? (
                              <button
                                type="button"
                                onClick={() => setForm((prev) => ({ ...prev, coverImageUrl: null }))}
                                disabled={saving || coverUploading}
                                className="text-start text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400"
                              >
                                {t('institution.categories.removeCover', 'Retirer l’image')}
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          {t('institution.common.description', 'Description')}
                        </label>
                        <textarea
                          value={form.description}
                          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                          placeholder={t('institution.categories.descShortPh', 'Décrivez l’usage de la catégorie')}
                          rows={3}
                          className={cn(fieldCls, 'resize-none')}
                        />
                      </div>
                    </div>

                    <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/90 px-5 py-4 dark:border-[#19335E] dark:bg-[#0C1C38]/80 sm:flex-row sm:justify-end sm:gap-3 sm:px-6">
                      <button
                        type="button"
                        onClick={() => !saving && !coverUploading && closeDrawer()}
                        disabled={saving || coverUploading}
                        className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-[#B6CAEC] dark:bg-[#122644] dark:text-slate-200 dark:hover:bg-[#19335E]"
                      >
                        {t('institution.common.cancel', 'Annuler')}
                      </button>
                      <button
                        type="submit"
                        disabled={saving || coverUploading}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-[color:var(--inst-player-primary,#0C1C38)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[color:var(--inst-player-primary-hover,#19335E)] disabled:opacity-50"
                      >
                        {saving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : null}
                        {saving
                          ? t('institution.common.save', 'Sauvegarde…')
                          : editingId
                            ? t('institution.common.save', 'Enregistrer')
                            : t('institution.common.add', 'Ajouter')}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>,
            document.body,
          )}
        </>
      )}
    </div>
  );
}
